import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { langfuseApi } from '@/api/langfuse.api';
import { SlideOver } from '@/components/ui/slide-over';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import type { Value } from '@bufbuild/protobuf/wkt';
import { formatTimestampISO, formatLatency, parseMarkdown } from '@/utils/trace.utils';
import { parseContent, safeStringify, truncateLongStrings, uniqueParts, type MediaPart } from '@/utils/traceContent';
import { AttachmentsSummary, MediaParts } from '@/components/TraceMedia';

function JsonSection({ title, data }: { title: string; data: Value | undefined }) {
  const [expanded, setExpanded] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const { value: shortData, truncated } = useMemo(() => truncateLongStrings(data), [data]);
  if (!data) return null;
  const jsonStr = JSON.stringify(showFull ? data : shortData, null, 2);
  const isLong = jsonStr.length > 500;

  return (
    <div>
      <h3 className="text-sm font-medium text-muted-foreground mb-2">{title}</h3>
      <div className="bg-muted rounded-lg p-3 relative">
        <div className="absolute top-2 right-2 flex gap-3">
          {truncated && (
            <button onClick={() => setShowFull(!showFull)} className="text-xs text-blue-600 hover:text-blue-800">
              {showFull ? '省略长内容' : '显示完整内容'}
            </button>
          )}
          {isLong && (
            <button onClick={() => setExpanded(!expanded)} className="text-xs text-blue-600 hover:text-blue-800">
              {expanded ? '收起' : '展开'}
            </button>
          )}
        </div>
        <pre className={`text-xs overflow-x-auto ${expanded ? 'max-h-none' : 'max-h-40'}`}>
          {jsonStr}
        </pre>
      </div>
    </div>
  );
}

function TokenStats({ messages }: { messages: any[] }) {
  const totalInput = messages.reduce((sum, m) => sum + (m.usage_metadata?.input_tokens || 0), 0);
  const totalOutput = messages.reduce((sum, m) => sum + (m.usage_metadata?.output_tokens || 0), 0);
  const totalReasoning = messages.reduce(
    (sum, m) => sum + (m.usage_metadata?.output_token_details?.reasoning || 0),
    0
  );

  if (totalInput === 0 && totalOutput === 0) return null;

  return (
    <div className="bg-muted/50 rounded-lg p-3">
      <div className="text-xs font-medium text-muted-foreground mb-2">Token 消耗</div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="text-lg font-semibold">{totalInput.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">输入 Token</div>
        </div>
        <div>
          <div className="text-lg font-semibold">{totalOutput.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">输出 Token</div>
        </div>
        {totalReasoning > 0 && (
          <div>
            <div className="text-lg font-semibold text-orange-500">
              {totalReasoning.toLocaleString()}
            </div>
            <div className="text-xs text-muted-foreground">推理 Token</div>
          </div>
        )}
      </div>
      {totalInput + totalOutput > 0 && (
        <div className="mt-2 pt-2 border-t border-border/50 text-xs text-muted-foreground text-center">
          总计: {(totalInput + totalOutput).toLocaleString()} tokens
        </div>
      )}
    </div>
  );
}

function MsgBubble({ message, mediaIndex }: { message: any; mediaIndex?: Map<string, MediaPart> }) {
  const [expanded, setExpanded] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);

  const isHuman = message.type === 'human';
  const isTool = message.type === 'tool';
  const isAI = message.type === 'ai';
  const { text: content, thinking, parts } = useMemo(() => parseContent(message.content), [message.content]);
  const reasoningContent = [
    typeof message.additional_kwargs?.reasoning_content === 'string'
      ? message.additional_kwargs.reasoning_content
      : '',
    thinking,
  ]
    .filter(Boolean)
    .join('\n\n');
  const hasReasoning = !!reasoningContent;
  const hasToolCalls = message.tool_calls?.length > 0;
  const usage = message.usage_metadata;

  const isLong = content.length > 800;
  const displayContent = !expanded && isLong ? content.slice(0, 800) + '...' : content;

  const bubbleClass = isHuman
    ? 'bg-blue-500/10 border-blue-500/30 ml-0'
    : isTool
      ? 'bg-amber-500/10 border-amber-500/30 mx-auto max-w-[100%]'
      : 'bg-purple-500/10 border-purple-500/30 ml-auto mr-0';

  const textClass = isHuman
    ? 'text-blue-900 dark:text-blue-100'
    : isTool
      ? 'text-amber-900 dark:text-amber-100'
      : 'text-purple-900 dark:text-purple-100';

  return (
    <div className={`rounded-lg border p-3 ${bubbleClass}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded ${
              isHuman
                ? 'bg-blue-500/20 text-blue-600'
                : isTool
                  ? 'bg-amber-500/20 text-amber-600'
                  : 'bg-purple-500/20 text-purple-600'
            }`}
          >
            {isHuman ? '👤 用户' : isTool ? '🔧 工具' : '🤖 AI'}
          </span>
          {message.response_metadata?.model_name && (
            <span className="text-xs text-muted-foreground">
              {message.response_metadata.model_name}
            </span>
          )}
        </div>
        {message.response_metadata?.finish_reason && (
          <Badge variant="outline" className="text-xs">
            {message.response_metadata.finish_reason}
          </Badge>
        )}
      </div>

      <div className={`text-sm ${textClass} whitespace-pre-wrap break-words`}>
        {isTool ? (
          <div dangerouslySetInnerHTML={{ __html: parseMarkdown(displayContent) }} />
        ) : (
          displayContent ||
          (isAI && !content ? (
            <span className="italic text-muted-foreground">[思考中...]</span>
          ) : (
            ''
          ))
        )}
      </div>

      <MediaParts parts={parts} index={mediaIndex} />

      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-blue-600 hover:text-blue-800 mt-2"
        >
          {expanded ? '收起' : '展开全部'}
        </button>
      )}

      {hasReasoning && (
        <div className="mt-3 pt-3 border-t border-current/10">
          <button
            onClick={() => setShowReasoning(!showReasoning)}
            className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800"
          >
            <svg
              className={`w-3 h-3 transition-transform ${showReasoning ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            推理过程 {showReasoning ? '隐藏' : '查看'}
          </button>
          {showReasoning && (
            <div className="mt-2 text-xs text-muted-foreground/80 bg-orange-500/5 rounded p-2 whitespace-pre-wrap max-h-48 overflow-y-auto">
              {reasoningContent}
            </div>
          )}
        </div>
      )}

      {hasToolCalls && (
        <div className="mt-3 pt-3 border-t border-current/10">
          <div className="text-xs font-medium text-muted-foreground mb-2">工具调用</div>
          <div className="space-y-2">
            {message.tool_calls.map((tool: any, idx: number) => (
              <div key={tool.id || idx} className="bg-muted/50 rounded p-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-purple-600">{tool.name}</span>
                  <Badge variant="outline" className="text-xs">
                    tool_call
                  </Badge>
                </div>
                {tool.args && typeof tool.args === 'object' && (
                  <pre className="mt-1 text-xs text-muted-foreground overflow-x-auto">
                    {safeStringify(tool.args, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {usage && (usage.input_tokens > 0 || usage.output_tokens > 0) && (
        <div className="mt-3 pt-3 border-t border-current/10 flex gap-4 text-xs text-muted-foreground">
          <span>输入: {usage.input_tokens}</span>
          <span>输出: {usage.output_tokens}</span>
          {usage.output_token_details?.reasoning && (
            <span className="text-orange-500">推理: {usage.output_token_details.reasoning}</span>
          )}
        </div>
      )}
    </div>
  );
}

function getMessages(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data.filter(Boolean);
  if (data.messages) return data.messages.filter(Boolean);
  if (data.kind?.structValue?.messages) return data.kind.structValue.messages.filter(Boolean);
  if (data.kind?.listValue?.values) return data.kind.listValue.values.filter(Boolean);
  return [];
}


export interface TraceDetailsDialogProps {
  traceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TraceDetailsDialog({ traceId, open, onOpenChange }: TraceDetailsDialogProps) {
  const [showAllOutput, setShowAllOutput] = useState(false);
  const { data: traceDetails, isLoading } = useQuery({
    queryKey: ['trace-details', traceId],
    queryFn: async () => {
      if (!traceId) return null;
      return langfuseApi.getTraceDetails(traceId);
    },
    enabled: !!traceId && open,
  });

  const traceData = traceDetails as any;
  const inputData = traceData?.input;
  const outputData = traceData?.output;
  const inputMessages = getMessages(inputData);
  const outputMessages = getMessages(outputData);
  const lastOutputMessage =
    outputMessages.length > 0 ? [outputMessages[outputMessages.length - 1]] : [];
  const hiddenOutputCount = outputMessages.length - 1;
  const attachments = useMemo(
    () =>
      uniqueParts(
        [...getMessages(inputData), ...getMessages(outputData)].flatMap(m => parseContent(m?.content).parts)
      ).filter(p => p.kind !== 'unknown'),
    [inputData, outputData]
  );
  const mediaIndex = useMemo(() => new Map(attachments.map(p => [p.key, p])), [attachments]);

  const handleDownload = () => {
    if (!traceData || !traceId) return;
    const replacer = (_key: string, value: any) => {
      if (typeof value === 'bigint') return value.toString();
      return value;
    };
    const serializeMessage = (msg: any): any => {
      const obj: any = { type: msg.type };
      if (msg.content) obj.content = msg.content;
      if (msg.id) obj.id = msg.id;
      if (msg.name) obj.name = msg.name;
      if (msg.additional_kwargs && Object.keys(msg.additional_kwargs).length > 0)
        obj.additional_kwargs = msg.additional_kwargs;
      if (msg.response_metadata && Object.keys(msg.response_metadata).length > 0)
        obj.response_metadata = msg.response_metadata;
      if (msg.tool_calls?.length) obj.tool_calls = msg.tool_calls;
      if (msg.invalid_tool_calls?.length) obj.invalid_tool_calls = msg.invalid_tool_calls;
      if (msg.usage_metadata) obj.usage_metadata = msg.usage_metadata;
      if (msg.artifact !== undefined) obj.artifact = msg.artifact;
      if (msg.status) obj.status = msg.status;
      if (msg.tool_call_id) obj.tool_call_id = msg.tool_call_id;
      return obj;
    };

    const downloadData = {
      id: traceData.id,
      timestamp: traceData.timestamp,
      name: traceData.name,
      input: {
        adk_compression_state: { compact_windows: [], raw_events: [] },
        messages: inputMessages.map(serializeMessage),
      },
      output: {
        adk_compression_state: { compact_windows: [], raw_events: [] },
        messages: outputMessages.map(serializeMessage),
      },
    };

    const jsonStr = JSON.stringify(downloadData, replacer);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trace_${traceId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title="Trace 详情"
      description={`Trace ID: ${traceId}`}
      actions={
        <button
          onClick={handleDownload}
          className="p-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-muted-foreground hover:text-foreground"
          title="下载 JSON"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        </button>
      }
    >
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : traceDetails ? (
        <div className="space-y-6 overflow-y-auto max-h-[calc(100vh-120px)] pr-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-lg p-3 border border-blue-500/20">
              <div className="text-xs text-muted-foreground mb-1">延迟</div>
              <div className="text-lg font-semibold text-blue-600">
                {formatLatency(traceData.latency)}
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 rounded-lg p-3 border border-purple-500/20">
              <div className="text-xs text-muted-foreground mb-1">环境</div>
              <div className="text-lg font-semibold truncate">{traceData.environment || '-'}</div>
            </div>
            <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 rounded-lg p-3 border border-green-500/20">
              <div className="text-xs text-muted-foreground mb-1">时间</div>
              <div className="text-sm font-semibold truncate">
                {formatTimestampISO(traceData.timestamp)}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 text-xs">
            <span className="text-muted-foreground">Session:</span>
            <code className="bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
              {traceData.session_id || '-'}
            </code>
            {/* <span className="text-muted-foreground">User:</span>
            <code className="bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
              {traceData.user_id || '-'}
            </code> */}
          </div>

          {traceData.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {traceData.tags.map((tag: string) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          <TokenStats messages={outputMessages} />

          <AttachmentsSummary parts={attachments} />

          <div className="border rounded-lg">
            <div className="px-4 py-3 text-sm font-medium flex items-center gap-2 bg-green-500/5 rounded-t-lg border-b">
              <svg
                className="w-4 h-4 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              输入 - {inputMessages.length} 条消息
            </div>
            <div className="px-4 pb-4">
              {inputMessages.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {inputMessages.map((msg: any, idx: number) => (
                    <MsgBubble key={msg.id || idx} message={msg} mediaIndex={mediaIndex} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4">无输入消息</p>
              )}
            </div>
          </div>

          {hiddenOutputCount > 0 && (
            <div className="flex justify-center py-3">
              <button
                onClick={() => setShowAllOutput(!showAllOutput)}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                {showAllOutput ? (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 5v14l7-7 7 7V5z" />
                    </svg>
                    收起
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                    Show {hiddenOutputCount} more...
                  </>
                )}
              </button>
            </div>
          )}

          {showAllOutput &&
            outputMessages
              .slice(0, -1)
              .map((msg: any, idx: number) => <MsgBubble key={msg.id || idx} message={msg} mediaIndex={mediaIndex} />)}

          {hiddenOutputCount > 0 && showAllOutput && (
            <div className="flex justify-center py-3">
              <button
                onClick={() => setShowAllOutput(!showAllOutput)}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                {
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 5v14l7-7 7 7V5z" />
                    </svg>
                    收起
                  </>
                }
              </button>
            </div>
          )}

          <div className="border rounded-lg">
            <div className="px-4 py-3 text-sm font-medium flex items-center gap-2 bg-purple-500/5 rounded-t-lg border-b">
              <svg
                className="w-4 h-4 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              输出
            </div>
            <div className="px-4 pb-4 pt-3">
              {lastOutputMessage.length > 0 ? (
                <div className="space-y-3">
                  {lastOutputMessage.map((msg: any, idx: number) => (
                    <MsgBubble key={msg.id || idx} message={msg} mediaIndex={mediaIndex} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4">无回复</p>
              )}
            </div>
          </div>

          {traceData.metadata && <JsonSection title="完整元数据" data={traceData.metadata} />}
        </div>
      ) : (
        <p className="text-muted-foreground">无法加载详情</p>
      )}
    </SlideOver>
  );
}
